import { FastshiftExpectedError } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';

import { FourOhFourDisplay } from '@/common/displays';
import { FastshiftPageBreadcrumbs } from '@/common/fastshift-page-breadcrumbs/fastshift-page-breadcrumbs';
import { useRoutes } from '@/common/hooks';
import { getOrganizationDisplayName } from '@/common/organizations-flyout/src/utils';
import { useFastshiftSelector } from '@/state';

import { useOrganizationDetailsPageAnalytics } from './hooks/use-organization-details-page-analytics';
import { messages } from './messages';
import { OrganizationDcInstancesTable } from './organization-dc-instances-table/organization-dc-instances-table';
import { OrganizationSummary } from './summary/organization-summary';

export const FastshiftOrganizationDetailsPage = () => {
	useOrganizationDetailsPageAnalytics();
	const { formatMessage } = useIntl();
	const { paths } = useRoutes();

	const { domainId, organizationId } = useParams<{
		domainId: string;
		organizationId: string;
	}>();
	const organizationDetail = useFastshiftSelector((state) => state.organizationDetail);

	if (domainId === undefined || organizationId === undefined) {
		return <FourOhFourDisplay />;
	}

	const hasMatchingOrganizationDetail =
		organizationDetail.domainId === domainId && organizationDetail.orgId === organizationId;
	const organizationSummary = hasMatchingOrganizationDetail
		? organizationDetail.summary.data
		: null;
	const isOrganizationNameLoading =
		!hasMatchingOrganizationDetail || organizationDetail.summary.pending;
	const organizationName = getOrganizationDisplayName(
		{
			orgId: organizationId,
			orgName: organizationSummary?.orgName ?? null,
		},
		{ formatMessage },
	);
	const isNotFound =
		hasMatchingOrganizationDetail &&
		(organizationDetail.summary.error === FastshiftExpectedError.NOT_FOUND ||
			organizationDetail.dcInstances.error === FastshiftExpectedError.NOT_FOUND);

	if (isNotFound) {
		return <FourOhFourDisplay />;
	}

	return (
		<Stack space="space.300">
			<FastshiftPageBreadcrumbs
				isLoading={isOrganizationNameLoading}
				items={[
					{
						href: paths.domainScopedOrganizations ?? '',
						text: formatMessage(messages.organizationsListBreadcrumb),
					},
					{
						href: paths.domainScopedOrganizationDetails ?? '',
						text: organizationName,
					},
				]}
				loadingLabel={formatMessage(messages.loadingLabel)}
				skeletonWidths={[100, 120]}
				testId="organization-breadcrumb-skeleton"
			/>
			<Stack space="space.150">
				{isOrganizationNameLoading ? (
					<Skeleton width="300px" height="32px" testId="organization-name-skeleton" />
				) : (
					<Heading size="large" as="h1">
						{organizationName}
					</Heading>
				)}
			</Stack>
			<OrganizationSummary domainId={domainId} orgId={organizationId} />
			<OrganizationDcInstancesTable domainId={domainId} orgId={organizationId} />
		</Stack>
	);
};
